// 戦闘の初期状態を作る：盤面・本陣・キャンプ・龍脈・ガーディアン・自分と敵のヒーロー。
import type { BattleLoadout, BattleSetup, BuildingType, ItemId, Side } from '../types';
import { BUILDING_TYPES, CHAR_IDS, DICE_MOD_KEYS, DICE_MOD_RANGE, GROUND_KINDS, ITEM_IDS } from '../types';
import { CHARS } from '../data/chars';
import { ENEMIES } from '../data/enemies';
import { randInt, shuffle } from '../rng';
import { CFG } from './config';
import { CELLS, ROWS, cellAt, dist, mirror, neighbors, rowWidth } from './hex';
import { LAYOUTS, PROLOGUE_STAGE, curveAt, rivalAiType, stageDef, type StageDef } from './stages';
import { PROLOGUE } from './prologue';
import { JUTSU_BY_ID } from '../data/jutsu';
import type { Actor, Battle, HeroSpec, SurgeHooks, Tile } from './state';
import { equipEnemyType, interval, rulesOf } from './rules';

const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);

/**
 * 妖力を伸ばすスキルのフラグを読む（キャラ別スキルツリーの受け口）：
 *   surge:fast:-1                 段階2以上の必要ポイント -1（値は足し合わせる）
 *   surge:boost:<キー>:<段>=1.5   その段で解放される特性の効果を1.5倍（キーは自分のキャラID。違うキャラのものは無視）
 *   surge:on<段>:<効果>           その段に上がったときの追加効果（heal=全回復 / mat=陣材+5 / skill=術の待ち0）
 */
export function parseSurgeHooks(flags: string[], key: string): SurgeHooks | undefined {
  const h: SurgeHooks = { pointDelta: 0, boost: {}, on: {} };
  let any = false;
  for (const f of flags) {
    if (typeof f !== 'string' || !f.startsWith('surge:')) continue;
    const parts = f.split(':');
    if (parts[1] === 'fast') {
      const v = Number(parts[2] ?? -1);
      if (Number.isFinite(v)) (h.pointDelta += Math.round(v)), (any = true);
    } else if (parts[1] === 'boost' && parts.length >= 4) {
      const [stageStr, valStr] = parts[3].split('=');
      const st = Number(stageStr);
      const v = Number(valStr ?? 1);
      if (parts[2] === key && Number.isInteger(st) && Number.isFinite(v) && v >= 0) (h.boost[st] = (h.boost[st] ?? 1) * v), (any = true);
    } else {
      const m = parts[1]?.match(/^on(\d)$/);
      if (m && parts[2]) {
        const st = Number(m[1]);
        (h.on[st] ??= []).push(parts.slice(2).join(':'));
        any = true;
      }
    }
  }
  return any ? h : undefined;
}

/** お供の能力のフラグ comp:<キー>=<値> を読む（値は足し合わせ。数でないものは無視） */
export function parseCompanion(flags: string[]): Record<string, number> | undefined {
  let out: Record<string, number> | undefined;
  for (const f of flags) {
    if (typeof f !== 'string' || !f.startsWith('comp:')) continue;
    const [k, v] = f.slice(5).split('=');
    const n = Number(v ?? 1);
    if (!k || !Number.isFinite(n) || n <= 0) continue;
    (out ??= {})[k] = (out[k] ?? 0) + n;
  }
  return out;
}

/** 式神の定期の加護 boon:<種類>=<値>:<手番ごと>:<最大の重ね>（最初の1つだけ） */
export function parseBoon(flags: string[]): HeroSpec['boon'] {
  for (const f of flags) {
    if (typeof f !== 'string' || !f.startsWith('boon:')) continue;
    const [kv, every, max] = f.slice(5).split(':');
    const [k, v] = (kv ?? '').split('=');
    const value = Number(v);
    if ((k === 'atk' || k === 'def' || k === 'regen') && Number.isFinite(value) && value > 0)
      return { key: k, value: Math.min(0.5, value), every: Math.max(1, Math.floor(Number(every) || 3)), max: Math.max(1, Math.min(9, Math.floor(Number(max) || 3))) };
  }
  return undefined;
}

/** 式神の奥義のフラグ shiki:<条件>:<効果>=<値> を読む（条件は最初のものだけ。値は足し合わせ） */
export function parseOugi(flags: string[]): { trigger: string; effects: Record<string, number>; uses?: number } | undefined {
  let out: { trigger: string; effects: Record<string, number>; uses?: number } | undefined;
  for (const f of flags) {
    if (typeof f !== 'string' || !f.startsWith('shiki:')) continue;
    const [head, v] = f.slice(6).split('=');
    const [trigger, eff] = (head ?? '').split(':');
    const n = Number(v ?? 1);
    if (!trigger || !eff || !Number.isFinite(n) || n <= 0) continue;
    out ??= { trigger, effects: {} };
    if (out.trigger !== trigger) continue;
    if (eff === 'uses') out.uses = Math.max(out.uses ?? 0, Math.min(9, Math.floor(n)));
    else out.effects[eff] = (out.effects[eff] ?? 0) + n;
  }
  return out;
}

/** その術の系統の強化（flags の jpow:<術>=割合 の合計） */
function jutsuPow(flags: string[], id: string): number {
  let sum = 0;
  for (const f of flags) {
    if (typeof f !== 'string' || !f.startsWith(`jpow:${id}=`)) continue;
    const v = Number(f.split('=')[1]);
    if (Number.isFinite(v)) sum += v;
  }
  return sum;
}

/** loadout を戦闘中の性能にする（キャラ固有の特性 CFG.passives もここで足す） */
export function heroSpecFromLoadout(lo: BattleLoadout): HeroSpec {
  if (!CHAR_IDS.includes(lo.charId)) throw new Error(`unknown char ${lo.charId}`);
  const ch = CHARS[lo.charId];
  const sk = ch.skill;
  const passive = CFG.passives[lo.charId] ?? {};
  const st = lo.stats;
  const splashBonus = num(lo.skill?.splashBonus, 0);
  const spec: HeroSpec = {
    key: lo.charId,
    level: num(lo.level, 1),
    hpMax: Math.max(1, Math.round(num(st.hp, ch.base.hp))),
    atk: Math.max(0, Math.round(num(st.atk, ch.base.atk))),
    def: Math.max(0, Math.round(num(st.def, ch.base.def))),
    agi: Math.max(0, Math.round(num(st.agi, ch.base.agi))),
    move: Math.max(0, Math.round(num(st.move, ch.base.move))),
    range: Math.max(1, Math.round(num(st.range, ch.base.range))),
    magic: !!ch.base.magic,
    splash: ch.base.splash ?? 0,
    skill: {
      name: sk.name,
      mult: sk.mult * num(lo.skill?.power, 1),
      range: sk.range,
      cd: Math.max(1, sk.cd + Math.round(num(lo.skill?.cdDelta, 0))),
      magic: !!sk.magic,
      splash: Math.max(0, (sk.splash ?? 0) + splashBonus, (lo.flags ?? []).includes('ougi:kazamaru_senpu') ? CFG.flags.senpuSplash : 0),
      delay: sk.delay ?? 0,
      leech: sk.leech ?? 0,
      coreMult: sk.coreMult ?? 1,
      guard: false,
      heal: 0,
      execute: 0,
      line: !!sk.line,
      pierce: sk.pierce ?? 0,
      buildingMult: sk.buildingMult ?? 1,
    },
    build: {
      unlocked: (lo.build?.unlocked ?? []).filter((t) => BUILDING_TYPES.includes(t)),
      levels: { ...(lo.build?.levels ?? {}) },
      maxCount: Math.max(0, Math.round(num(lo.build?.maxCount, 4))),
      perTurn: Math.max(0, Math.round(num(lo.build?.perTurn, 1))),
      costMult: Math.max(0.1, num(lo.build?.costMult, 1)),
      costFlat: Math.max(0, Math.round(num(lo.build?.costFlat, 0))),
      hpMult: Math.max(0.1, num(lo.build?.hpMult, 1)),
      powerMult: Math.max(0, num(lo.build?.powerMult, 1)),
      range: Math.max(1, Math.round(num(lo.build?.range, 1))) + (passive.buildRange ?? 0),
    },
    // 薬の回復量：1以下なら最大HPに対する割合（meta の形式）、それより大きければHPの値
    potionHeal: (() => {
      const h = Math.max(0, num(lo.potions?.heal, 0.35));
      const max = Math.max(1, Math.round(num(st.hp, ch.base.hp)));
      return Math.round(h <= 1 ? max * h : h);
    })(),
    diceMin: Math.min(4, Math.max(1, Math.round(num(lo.diceMin, 1)))),
    dice: Object.fromEntries(DICE_MOD_KEYS.map((k) => [k, Math.max(DICE_MOD_RANGE[k][0], Math.min(DICE_MOD_RANGE[k][1], num(lo.dice?.[k], 0)))]).filter(([, v]) => v)),
    respawnDelta: num(lo.respawnDelta, 0),
    territoryBonus: num(lo.territoryBonus, 0),
    mult: {
      core: num(lo.mult?.core, 1) * (passive.coreMult ?? 1),
      boss: num(lo.mult?.boss, 1),
      neutral: num(lo.mult?.neutral, 1),
      taken: num(lo.mult?.taken, 1),
    },
    lifesteal: Math.max(0, num(lo.lifesteal, 0)),
    flags: [...(lo.flags ?? [])],
    matIncome: Math.round(num(lo.materials?.incomeBonus, 0)),
    trait: lo.charId,
    surgeHooks: parseSurgeHooks(lo.flags ?? [], lo.charId),
    comp: parseCompanion(lo.flags ?? []),
    ougi: parseOugi(lo.flags ?? []),
    boon: parseBoon(lo.flags ?? []),
  };
  // 持っていく術が自分を中心に放つ術なら、術の名前・待ちを差し替える（威力はスキルの術の強化 power を掛ける）
  const ju = typeof lo.jutsu === 'string' ? JUTSU_BY_ID[lo.jutsu] : undefined;
  if (ju && ju.charId === lo.charId && ju.kind !== 'strike') {
    spec.skill = {
      ...spec.skill,
      name: ju.name,
      cd: Math.max(1, ju.cd + Math.round(num(lo.skill?.cdDelta, 0))),
      range: 0,
      splash: 0,
      delay: 0,
      leech: 0,
      line: false,
      pierce: 0,
      cast: ju.id,
      // スキルの術の強化 × その術の系統の強化（jpow:<術>=割合。専用ツリーの列）
      power: num(lo.skill?.power, 1) * (1 + jutsuPow(lo.flags ?? [], ju.id)),
    };
  }
  // 奥義の名前は持っていく術の奥義（基本の術なら CFG の名前のまま）
  if (ju && ju.charId === lo.charId && ju.kind !== 'strike') spec.ougiName = ju.ougiName;
  return spec;
}

/** 敵ヒーロー：章の rival を章内の番号から決まる Lv で作る（固定成長・Lv で覚える技） */
export function rivalSpec(def: StageDef, rules = 0): HeroSpec {
  const tpl = CFG.rivals[def.rival.template];
  const R = CFG.rival;
  const lv = def.rival.lv;
  const L = lv - 1;
  // 能力は敵の基準曲線で伸びる（HP・防御は curve.atk、攻撃は curve.hp）。Lv は技の習得と速さに使う
  let hp = R.base.hp * (tpl.hpMult ?? 1) * curveAt(def.id, 'atk');
  let atk = R.base.atk * (tpl.atkMult ?? 1) * curveAt(def.id, 'hp');
  let defv = R.base.def * curveAt(def.id, 'atk');
  // 版3：傾向ごとの性質（v3.rivalAi[傾向].traits。本陣速攻は本陣への与ダメが高くHPが低い、など。BALANCE.md 40章）
  const ai = rivalAiType(def.id, rules);
  const T = ai?.traits ?? {};
  const agi = Math.round(tpl.agi * (1 + R.growth.agi * L)) + (T.agi ?? 0);
  const flags: string[] = [];
  let lifesteal = 0;
  let cdDelta = 0;
  for (const [at, tech] of Object.entries(tpl.learn)) {
    if (Number(at) > lv) continue;
    if (tech === 'might') atk *= 1 + R.tech.might;
    else if (tech === 'iron') defv *= 1 + R.tech.iron;
    else if (tech === 'vigor') hp *= 1 + R.tech.vigor;
    else if (tech === 'leech') lifesteal = 0.2;
    else if (tech === 'focus') cdDelta -= 1;
    else flags.push(tech);
  }
  defv = Math.round(defv);
  const d = def.difficulty;
  // 版3：構え（術の予告）と最初の手番の遅れで武将が弱くなった分の底上げ（v3.rivalPower）
  const v3p = rules >= 3 ? (CFG.v3.rivalPower[def.chapter - 1] ?? 1) : 1;
  const first = def.id === 1 ? CFG.firstStageEnemy : 1;
  hp = Math.round(hp * def.rival.power * v3p * first * (T.hp ?? 1) * (1 + CFG.difficulty.hp * d));
  atk = Math.round(atk * def.rival.power * v3p * first * (T.atk ?? 1) * (1 + CFG.difficulty.atk * d));
  for (const f of T.flags ?? []) if (!flags.includes(f)) flags.push(f);
  const c = def.chapter;
  const RB = CFG.rivalBuild;
  const unlocked: BuildingType[] = RB.unlock.slice(0, Math.min(c, RB.unlock.length)).flat();
  const blv = Math.floor((c - 1) / 2);
  const s = tpl.skill;
  return {
    key: def.rival.enemyId,
    level: lv,
    hpMax: hp,
    atk,
    def: defv,
    agi,
    move: 0,
    range: tpl.range,
    magic: !!tpl.magic,
    splash: 0,
    skill: {
      name: s.name,
      mult: s.mult,
      range: s.range,
      cd: Math.max(1, s.cd + cdDelta),
      magic: !!s.magic,
      splash: s.splash ?? 0,
      delay: s.delay ?? 0,
      leech: s.leech ?? 0,
      coreMult: 1,
      guard: !!s.guard,
      heal: s.heal ?? 0,
      execute: s.execute ?? 0,
      line: false,
      pierce: 0,
      buildingMult: 1,
    },
    build: {
      unlocked,
      levels: Object.fromEntries(unlocked.map((t) => [t, blv])),
      maxCount: RB.maxCount + Math.floor(c / RB.maxPerChapters) + (ai?.buildMax ?? 0),
      perTurn: 1,
      costMult: 1,
      costFlat: 0,
      hpMult: T.buildHp ?? 1,
      powerMult: (1 + RB.powerPerChapter * (c - 1)) * (T.buildPower ?? 1),
      range: 1,
    },
    potionHeal: Math.round(hp * 0.3),
    diceMin: 1,
    dice: {},
    respawnDelta: 0,
    territoryBonus: 0,
    mult: { core: T.core ?? 1, boss: 1, neutral: T.neutral ?? 1, taken: T.taken ?? 1, ...(T.hero ? { hero: T.hero } : {}) },
    ...(T.devour ? { devour: T.devour } : {}),
    lifesteal,
    flags,
    matIncome: 0,
    trait: `rival_${CFG.surge.rivalTraits[def.rival.template] ?? 'bu'}`,
  };
}

/** 中立・ガーディアン・増援の能力 */
export function neutralStats(enemyId: string, tier: 'small' | 'medium' | 'elite' | 'boss' | 'raider', def: StageDef) {
  const base = CFG.neutrals[tier];
  const g = CFG.neutralGrowth;
  const no = ENEMIES[enemyId]?.no ?? 0;
  const fl = CFG.neutralFlavor[no % CFG.neutralFlavor.length];
  const d = def.difficulty;
  const idx = 1 + g.index * (def.index - 1);
  // 敵の型：重装は硬く遅い、攻城（増援のとき）は硬い
  const type = ENEMIES[enemyId]?.type;
  const T = CFG.enemyTypes;
  const hpM = type === 'heavy' ? T.heavy.hp : type === 'siege' && tier === 'raider' ? T.siege.hp : 1;
  const agiD = type === 'heavy' ? T.heavy.agi : 0;
  const first = def.id === 1 ? CFG.firstStageEnemy : 1;
  return {
    hp: Math.round(base.hp * curveAt(def.id, 'atk') * idx * fl.hp * hpM * first * (1 + CFG.difficulty.hp * d)),
    atk: Math.round(base.atk * curveAt(def.id, 'hp') * idx * fl.atk * first * (1 + CFG.difficulty.atk * d)),
    def: Math.round(base.def * curveAt(def.id, 'atk')),
    agi: Math.max(0, base.agi + fl.agi + agiD),
  };
}

function parseLayout(name: string) {
  const top = LAYOUTS[name];
  const grid: { ch: string; side: Side }[][] = [];
  for (let row = 0; row < ROWS; row++) {
    if (row <= 5) grid.push(top[row].split('').map((ch) => ({ ch, side: 'red' as Side })));
    else {
      const src = top[ROWS - 1 - row];
      const w = rowWidth(row);
      grid.push(Array.from({ length: w }, (_, col) => ({ ch: src[w - 1 - col], side: 'blue' as Side })));
    }
  }
  return grid;
}

function heroActor(b: Battle, side: Side, spec: HeroSpec, enemyId: string | null, potions: number): Actor {
  return {
    id: b.nextId++,
    kind: 'hero',
    side,
    enemyId,
    cell: b.homes[side],
    hp: spec.hpMax,
    hpMax: spec.hpMax,
    atk: spec.atk,
    def: spec.def,
    agi: spec.agi,
    alive: true,
    nextAt: 0,
    defending: false,
    spec,
    turns: 0,
    skillReady: 0,
    respawnAt: null,
    moved: 0,
    riposte: false,
    builtThisTurn: 0,
    potions,
  };
}

function neutralActor(b: Battle, kind: 'neutral' | 'boss', enemyId: string, tier: Actor['tier'], cell: number, st: { hp: number; atk: number; def: number; agi: number }): Actor {
  return equipEnemyType({
    id: b.nextId++,
    kind,
    side: 'neutral',
    enemyId,
    cell,
    camp: cell,
    hp: st.hp,
    hpMax: st.hp,
    atk: st.atk,
    def: st.def,
    agi: st.agi,
    alive: true,
    nextAt: 0,
    defending: false,
    tier,
    engaged: false,
    absence: 0,
    dmg: { blue: 0, red: 0 },
    telegraph: null,
    pattern: 0,
    lastHit: null,
  });
}

export function createBattleState(setup: BattleSetup): Battle {
  const def = stageDef(setup.stageId, setup.difficulty ?? 0);
  const playerSpec = heroSpecFromLoadout(setup.loadout);
  const grid = parseLayout(def.layout);
  const b: Battle = {
    v: 1,
    setup: JSON.parse(JSON.stringify(setup)),
    rng: setup.seed >>> 0,
    chapter: def.chapter,
    index: def.index,
    rule: def.rule,
    layout: def.layout,
    time: 0,
    turnCount: 0,
    phase: 'idle',
    current: null,
    roll: null,
    budget: 0,
    bonus: 0,
    reach: null,
    tiles: [],
    cores: {} as Battle['cores'],
    homes: {} as Battle['homes'],
    actors: [],
    nextId: 1,
    prio: {},
    mat: { blue: Math.max(0, Math.round(num(setup.loadout.materials?.start, CFG.material.start))), red: CFG.rival.materialsStart },
    siege: { blue: 0, red: 0 },
    waves: def.waves.slice(),
    wavesDone: 0,
    raider: def.raiderId ? { enemyId: def.raiderId, ...neutralStats(def.raiderId, 'raider', def) } : null,
    winner: null,
    reason: null,
    stats: {
      playerTurns: 0,
      heroKOs: 0,
      kills: {},
      bossKilled: false,
      enemyBuildingsDestroyed: 0,
      buildingsBuilt: 0,
      rivalBuildingsBuilt: 0,
      rivalKOs: 0,
      coreDamage: 0,
      tiles: 0,
      chests: [],
    },
    surge: { blue: { stage: 1, points: 0 }, red: { stage: 1, points: 0 } },
    log: [],
  };
  const camps: { cell: number; fill: NonNullable<StageDef['camps']['a']> }[] = [];
  for (const c of CELLS) {
    const { ch, side } = grid[c.row][c.col];
    const tile: Tile = { t: 'grass', owner: null, side: null, pickup: null, relay: false, v: randInt(b, 4) };
    if (ch === '#') tile.t = 'block';
    else if (ch === 'C' && def.mode === 'defense' && side === 'red') {
      // 防衛戦は敵本陣を置かない（攻め手の出どころの目印として位置だけ持つ）
      b.cores.red = { cell: c.i, hp: def.coreHp, max: def.coreHp, def: def.coreDef };
    } else if (ch === 'C') {
      tile.t = 'core';
      tile.side = side;
      const hp = def.mode === 'defense' && side === 'blue' ? Math.round(def.coreHp * (CFG.defense.coreMult[CFG.defense.chapters.indexOf(def.chapter)] ?? 1)) : def.coreHp;
      b.cores[side] = { cell: c.i, hp, max: hp, def: def.coreDef };
    } else if (ch === 'H' && def.mode === 'defense' && side === 'red') {
      // 防衛戦は敵ヒーローがいないので帰還マスも置かない（攻め手の出どころの目印として位置だけ持つ）
      b.homes.red = c.i;
    } else if (ch === 'H') {
      tile.t = 'home';
      tile.side = side;
      b.homes[side] = c.i;
    } else if (ch === 'a' || ch === 'm' || ch === 'e' || ch === 'B') {
      const fill = def.camps[ch];
      if (fill) {
        tile.t = 'camp';
        camps.push({ cell: c.i, fill });
      }
    } else if (ch === '$') tile.pickup = 'chest';
    else if (ch === '+') tile.pickup = 'shrine';
    b.tiles.push(tile);
  }
  // 龍脈：4行目の外側（と180°反対側）、中央キャンプ
  const candidates = CELLS.filter((c) => b.tiles[c.i].t === 'grass' && !b.tiles[c.i].pickup);
  const approach = candidates
    .filter((c) => c.row === 4 && neighbors(c.i).filter((n) => b.tiles[n].t !== 'block').length >= 3)
    .sort((x, y) => Math.abs(y.col - 2.5) - Math.abs(x.col - 2.5) || x.i - y.i)[0];
  if (approach) {
    b.tiles[approach.i].relay = true;
    b.tiles[cellAt(...mirror(approach.col, approach.row))!].relay = true;
  }
  b.tiles[cellAt(3, 5)!].relay = true;
  placeGround(b, def, setup.stageId === PROLOGUE_STAGE);

  // プロローグは酒呑童子（性能はスズネを元にした loadout、盤の上の絵と名前は enemyId で差し替える。妖力の特性は無し）
  const prologue = setup.stageId === PROLOGUE_STAGE;
  if (prologue) {
    playerSpec.trait = undefined;
    playerSpec.look = PROLOGUE.look;
    playerSpec.skill.name = PROLOGUE.skillName;
    playerSpec.ougiName = PROLOGUE.ougiName;
  }
  const blue = heroActor(b, 'blue', playerSpec, prologue ? PROLOGUE.heroEnemyId : null, Math.max(0, Math.round(num(setup.loadout.potions?.count, 2))));
  if (prologue && b.surge) {
    b.surge.blue = { stage: 1, points: PROLOGUE.surgePoints };
    blue.surge = 1;
  }
  // アイテム（古い setup は回復薬だけ）
  if (Array.isArray(setup.loadout.items)) {
    const items: Partial<Record<ItemId, number>> = {};
    for (const it of setup.loadout.items) if (ITEM_IDS.includes(it?.id) && !(it.id in items)) items[it.id] = Math.max(0, Math.min(9, Math.round(num(it.count, 0))));
    blue.items = items;
    blue.potions = items.heal ?? 0;
  }
  b.actors.push(blue);
  // 防衛戦は敵ヒーローを出さない（攻め手は波で来る）
  if (def.mode === 'defense') {
    const D = CFG.defense;
    playerSpec.build.maxCount = Math.round(playerSpec.build.maxCount * D.buildMaxMult);
    // ルールの版3からは攻め手が強い（型の向き不向きが出る。CFG.v3）
    const cm = (D.chapterMult[D.chapters.indexOf(def.chapter)] ?? 1) * ((setup.rules ?? 0) >= 3 ? CFG.v3.defenseMult : 1);
    const unit = (enemyId: string, tier: 'small' | 'medium' | 'elite') => {
      const st = neutralStats(enemyId, tier, def);
      return { enemyId, hp: Math.round(st.hp * D.hp * cm), atk: Math.round(st.atk * D.atk * cm), def: st.def, agi: st.agi };
    };
    const A = def.attackers!;
    b.defense = {
      wave: 0,
      total: D.waves.length,
      pool: { small: A.small.map((id) => unit(id, 'small')), medium: A.medium.map((id) => unit(id, 'medium')), elite: A.elite.map((id) => unit(id, 'elite')) },
      leader: null,
    };
  }
  // プロローグは敵の総大将を出さない（一本道の邪魔をしない）
  else if (!prologue) {
    const rival = heroActor(b, 'red', rivalSpec(def, setup.rules ?? 0), def.rival.enemyId, 2);
    b.actors.push(rival);
  }
  // プロローグの人間の軍：決めたマスに置き、持ち場から動かさない（案内の一本道）
  if (prologue)
    for (const f of PROLOGUE.foes) {
      const st = neutralStats(f.enemyId, f.tier, def);
      const u = neutralActor(b, 'neutral', f.enemyId, f.tier, cellAt(...f.at)!, { ...st, hp: Math.round(st.hp * f.hp) });
      u.hold = true;
      b.actors.push(u);
    }
  for (const camp of camps) b.actors.push(neutralActor(b, 'neutral', camp.fill.enemyId, camp.fill.tier, camp.cell, neutralStats(camp.fill.enemyId, camp.fill.tier, def)));
  if (b.defense) {
    // 防衛戦の頭目は第3波で出る。番人がいればその妖、いなければ章のエリート。強さは番人と同じ
    const id = def.guardian?.enemyId ?? def.attackers!.elite[def.index % def.attackers!.elite.length];
    const st = neutralStats(id, 'elite', def);
    b.defense.leader = { enemyId: id, hp: Math.round(st.hp * CFG.eliteGuardian.hp), atk: Math.round(st.atk * CFG.eliteGuardian.atk), def: st.def, agi: st.agi };
  } else if (def.guardian) {
    // ガーディアン：敵本陣から2マス手前に立ち、生きている間は敵本陣に結界
    const core = b.cores.red.cell;
    const cell = CELLS.filter(
      (c) => dist(c.i, core) === 2 && b.tiles[c.i].t === 'grass' && !b.tiles[c.i].pickup && !b.tiles[c.i].relay && !b.actors.some((u) => u.cell === c.i),
    ).sort((x, y) => y.row - x.row || Math.abs(x.col - 3) - Math.abs(y.col - 3) || x.i - y.i)[0].i;
    b.tiles[cell].t = 'boss';
    b.tiles[cell].owner = 'red'; // 番人の持ち場は最初から敵の色（中立の妖のマスは色なし）。陣地率には数えない（grass ではない）
    const g = def.guardian;
    let st = neutralStats(g.enemyId, g.tier === 'boss' ? 'boss' : 'elite', def);
    if (g.tier === 'elite') st = { ...st, hp: Math.round(st.hp * CFG.eliteGuardian.hp), atk: Math.round(st.atk * CFG.eliteGuardian.atk) };
    else {
      const m = CFG.rival.bossStage[def.chapter - 1] ?? 1;
      st = { ...st, hp: Math.round(st.hp * m), atk: Math.round(st.atk * m) };
    }
    const ga = neutralActor(b, 'boss', g.enemyId, 'boss', cell, st);
    ga.side = 'red';
    ga.bossPattern = g.pattern;
    b.actors.push(ga);
  }
  shuffle(b, b.actors.map((a) => a.id)).forEach((id, n) => (b.prio[id] = n));
  for (const a of b.actors) a.nextAt = interval(b, a);
  // 版3：敵の武将の最初の手番は少し遅く（自分が先に動ける）
  if (rulesOf(b) >= 3 && CFG.v3.rivalFirstDelay > 0) for (const a of b.actors) if (a.kind === 'hero' && a.side === 'red') a.nextAt = Math.round(interval(b, a) * (1 + CFG.v3.rivalFirstDelay));
  return b;
}

/**
 * 土地ギミック（上の難易度の戦場だけ。防衛戦・プロローグは無し）：面ごとに決まった並びから perDifficulty[d] 種類を選び、
 * 1種類につき上半分（2〜4行）の1マスと、その mirror の1マスに置く。乱数は使わない（面と難易度で決まる。戦闘の乱数の流れも変えない）
 */
function placeGround(b: Battle, def: StageDef, prologue: boolean) {
  const n = def.mode || prologue ? 0 : (CFG.ground.perDifficulty[def.difficulty] ?? 0);
  if (n <= 0) return;
  const kinds = GROUND_KINDS.map((_, i) => GROUND_KINDS[(i + def.id) % GROUND_KINDS.length]).slice(0, n);
  const ok = (i: number) => {
    const t = b.tiles[i];
    return t.t === 'grass' && !t.pickup && !t.relay && !t.ground && dist(i, b.homes.blue) >= 2 && dist(i, b.homes.red) >= 2;
  };
  let h = (Math.imul(def.id * 31 + def.difficulty, 2654435761) >>> 0) || 1;
  for (const kind of kinds) {
    const cands = CELLS.filter((c) => {
      if (c.row < 2 || c.row > 4 || !ok(c.i)) return false;
      const m = cellAt(...mirror(c.col, c.row));
      // 同じ種類・ほかの土地と隣り合わないように
      return m != null && m !== c.i && ok(m) && !neighbors(c.i).some((x) => b.tiles[x].ground);
    });
    if (!cands.length) return;
    h = (Math.imul(h ^ (h >>> 15), 2246822519) >>> 0) || 1;
    const c = cands[h % cands.length];
    b.tiles[c.i].ground = kind;
    b.tiles[cellAt(...mirror(c.col, c.row))!].ground = kind;
  }
}
